import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashCloneDeep from "lodash/cloneDeep";
import Vue from "vue";

import { ENDPOINT_SAVED_SEARCH } from "@/common/constants/api";
import { DAYS_OF_WEEK } from "@/common/constants/misc";
import { httpDelete, httpGet, httpPost, httpPut } from "@/services/api.service";
import store from "@/store";
import {
  MODULE_PREFIX_NOTIFICATIONS,
  MODULE_PREFIX_SAVED_SEARCH,
} from "@/store";
import { SET_NOTIFICATION_STEP } from "@/store/mutations.type";
import { MODIFY_SAVED_SEARCH_QUERY } from "@/store/actions.type";

export { _saveSearchQuery as saveSearchQuery };
export { _getSavedSearchList as getSavedSearchList };
export { _deleteSearchQuery as deleteSearchQuery };
export { _updateSearchQuery as updateSearchQuery };
export { _updateStoreStep as updateStoreStep };
export { _extractDetailsFromSteps as extractDetailsFromSteps };
export { _extractDetailsFromSavedSearch as extractDetailsFromSavedSearch };
export { _updateSavedSearchNotification as updateSavedSearchNotification };
export { _removeSavedSearchNotification as removeSavedSearchNotification };
export { _getKeywordsStringFromBuilder as getKeywordsStringFromBuilder };

const _saveSearchQuery = async (params) => {
  try {
    let res = await httpPost({
      url: ENDPOINT_SAVED_SEARCH,
      data: JSON.parse(JSON.stringify(params)),
    });
    const { data } = JSON.parse(res);
    if (lodashGet(data, "savedSearch")) {
      return data.savedSearch;
    }
    return true;
  } catch (error) {
    console.error("Unable to create search query", error);
    return false;
  }
};

const _getSavedSearchList = async () => {
  try {
    let res = await httpGet({
      url: ENDPOINT_SAVED_SEARCH,
      noCache: true,
    });
    const { data } = JSON.parse(res);
    if (lodashGet(data, "savedSearches")) {
      return data.savedSearches;
    }
    return true;
  } catch (error) {
    console.error("Unable to get users saved search", error);
    return false;
  }
};

const _deleteSearchQuery = async (params) => {
  const endpoint = ENDPOINT_SAVED_SEARCH + "/" + params.id;
  try {
    await httpDelete({
      url: endpoint,
    });
    return true;
  } catch (error) {
    console.error("Unable to delete search query", error);
    return false;
  }
};

const _updateSearchQuery = async (params) => {
  const endpoint = ENDPOINT_SAVED_SEARCH + "/" + params.id;
  try {
    let res = await httpPut({
      url: endpoint,
      data: params.data,
    });
    const { data } = JSON.parse(res);
    if (lodashGet(data, "savedSearch")) {
      return data.savedSearch;
    }
    return true;
  } catch (error) {
    console.error("Unable to update search query", error);
    return false;
  }
};

const _updateStoreStep = (notification, stepName) => {
  const steps = store.getters[MODULE_PREFIX_NOTIFICATIONS + "steps"];
  const step = lodashFind(steps, ["name", stepName]);

  if (notification && step && step.value) {
    let value = lodashCloneDeep(step.value);

    if (stepName === "frequency") {
      value.timeOfDay = null;
      value.dayOfWeek = null;
      if (notification.frequency) {
        value.frequency = notification.frequency;
        if (
          notification.timeOfDay &&
          (notification.frequency === "DAILY" ||
            notification.frequency === "WEEKLY")
        ) {
          value.timeOfDay = notification.timeOfDay;
          if (notification.frequency === "WEEKLY" && notification.dayOfWeek) {
            value.dayOfWeek = notification.dayOfWeek;
          }
        }
      } else {
        console.warn("SavedSearch.service.js: Frequency not set!");
      }

      value.expiration = notification.expiration;
      if (notification.expiration !== "never") {
        if (notification.expirationDate) {
          value.expirationDate = notification.expirationDate;
        }
      }

      if (
        notification.delivery &&
        Array.isArray(notification.delivery) &&
        notification.delivery.length
      ) {
        value.delivery = notification.delivery;
      } else {
        console.warn("SavedSearch.service.js: Delivery not set!");
      }

      if (notification.targets) {
        value.targets = lodashCloneDeep(notification.targets);
      }
    } else if (stepName === "sharing") {
      if (notification.shareWithColleagues) {
        value.shareWithColleagues = notification.shareWithColleagues;
      }
      if (notification.selectedMembers) {
        value.sharedList = lodashCloneDeep(notification.selectedMembers);
      }
    }

    value = JSON.parse(JSON.stringify(value));

    store.commit(MODULE_PREFIX_NOTIFICATIONS + SET_NOTIFICATION_STEP, {
      name: stepName,
      value: value,
    });
  }
};

const _extractDetailsFromSteps = (notification) => {
  const steps = store.getters[MODULE_PREFIX_NOTIFICATIONS + "steps"];

  if (notification && steps && Array.isArray(steps) && steps.length) {
    steps.forEach((step) => {
      if (step && step.value) {
        if (step.name === "frequency") {
          Vue.set(notification, "frequency", step.value.frequency);
          Vue.set(notification, "dayOfWeek", step.value.dayOfWeek);
          Vue.set(notification, "timeOfDay", step.value.timeOfDay);

          let expiration = "never";
          if (step.value.expiration) {
            expiration = step.value.expiration;
          }
          Vue.set(notification, "expiration", expiration);

          let expirationDate = null;
          if (step.value.expiration && step.value.expiration !== "never") {
            expirationDate = step.value.expirationDate
              ? step.value.expirationDate
              : dayjs().add(step.value.expiration, "week").toISOString();
          }
          Vue.set(notification, "expirationDate", expirationDate);
          Vue.set(
            notification,
            "deliveryEmail",
            step.value.delivery.includes("email")
          );
          Vue.set(
            notification,
            "deliveryPush",
            step.value.delivery.includes("push")
          );
          Vue.set(
            notification,
            "deliveryTarget",
            step.value.delivery.includes("target")
          );
          Vue.set(notification, "targets", lodashCloneDeep(step.value.targets));
        } else if (step.name === "sharing") {
          Vue.set(
            notification,
            "sharedList",
            lodashCloneDeep(step.value.sharedList)
          );
        }
      }
    });
  }
};

const _extractDetailsFromSavedSearch = (notification, stepName) => {
  const savedSearchDetails =
    store.getters[MODULE_PREFIX_SAVED_SEARCH + "savedSearchDetails"];

  if (!notification || !savedSearchDetails) {
    return;
  }

  notification.name = savedSearchDetails.name;
  notification.onlyFirstReport = lodashCloneDeep(
    savedSearchDetails.only_first_report
  );
  notification.pause = lodashCloneDeep(savedSearchDetails.pause);

  if (!stepName || stepName === "frequency") {
    if (savedSearchDetails.frequency) {
      Vue.set(notification, "frequency", savedSearchDetails.frequency);

      if (savedSearchDetails.schedule) {
        const parts = savedSearchDetails.schedule.split("_");
        if (parts && Array.isArray(parts) && parts.length > 0) {
          const dayOfWeekAbbr = parts.length > 2 ? parts[0] : null;
          if (dayOfWeekAbbr) {
            const dayOfWeek = lodashFind(DAYS_OF_WEEK, [
              "abbrev2",
              dayOfWeekAbbr,
            ]);
            if (dayOfWeek && dayOfWeek.name) {
              Vue.set(notification, "dayOfWeek", dayOfWeek.name);
            }
          }

          let timeOfDay = parts.length > 2 ? parts[1] : parts[0];
          timeOfDay = timeOfDay.replace("_", ":");
          Vue.set(notification, "timeOfDay", timeOfDay);
        }
      }

      let expiration = savedSearchDetails.expiration;
      if (!expiration) {
        expiration = "never";
      }
      Vue.set(notification, "expiration", expiration);

      const delivery = [];
      if (savedSearchDetails.use_email) {
        delivery.push("email");
      }
      if (savedSearchDetails.use_push) {
        delivery.push("push");
      }
      if (savedSearchDetails.use_integrations) {
        delivery.push("target");
      }
      Vue.set(notification, "delivery", delivery);
      Vue.set(
        notification,
        "targets",
        lodashCloneDeep(savedSearchDetails.integration_targets)
      );
    }
  }

  if (!stepName || stepName === "sharing") {
    const shareWithColleagues =
      savedSearchDetails.shared_list && savedSearchDetails.shared_list.length
        ? "yes"
        : "no";
    Vue.set(notification, "shareWithColleagues", shareWithColleagues);

    Vue.set(
      notification,
      "selectedMembers",
      lodashCloneDeep(savedSearchDetails.shared_list)
    );
  }
};

const _updateSavedSearchNotification = async (notification) => {
  let isSuccess = false;
  let isError = false;

  try {
    const savedSearchDetails =
      store.getters[MODULE_PREFIX_SAVED_SEARCH + "savedSearchDetails"];

    _extractDetailsFromSteps(notification);

    let data = {
      name: savedSearchDetails.name,
      use_email: notification.deliveryEmail,
      use_push: notification.deliveryPush,
      use_integrations: notification.deliveryTarget,
      only_first_report: savedSearchDetails.onlyFirstReport,
      pause: savedSearchDetails.pause,
      params: savedSearchDetails.params,
    };

    if (notification.frequency) {
      data.frequency = notification.frequency;
      if (
        notification.frequency === "IMMEDIATELY" ||
        notification.frequency === "HOURLY"
      ) {
        notification.timeOfDay = null;
        notification.dayOfWeek = null;
      } else if (notification.frequency === "DAILY") {
        notification.dayOfWeek = null;
      }

      if (notification.timeOfDay) {
        const timeOfDay = notification.timeOfDay.replace(":", "_");
        let schedule = timeOfDay;
        if (notification.dayOfWeek) {
          schedule = `${notification.dayOfWeek.slice(0, 2)}_${timeOfDay}`;
        }
        data.schedule = schedule;
      }
    }

    data.expiration = null;
    if (notification.expiration !== "never") {
      data.expiration = notification.expiration;
      if (notification.expirationDate) {
        data.expiration_date = notification.expirationDate;
      }
    }

    data.shared_list = null;
    if (notification.sharedList) {
      data.shared_list = notification.sharedList;
    }

    data.integration_targets = null;
    if (notification.targets) {
      data.integration_targets = notification.targets;
    }

    data = JSON.parse(JSON.stringify(data));

    await store.dispatch(
      MODULE_PREFIX_SAVED_SEARCH + MODIFY_SAVED_SEARCH_QUERY,
      {
        id: savedSearchDetails.saved_search_id,
        data: data,
      }
    );
    isSuccess = true;
  } catch (error) {
    const notificationErrorMessage =
      "Failed to add notifications details to saved search query";
    error.message = error.message
      ? `${notificationErrorMessage}: ${error.message}`
      : notificationErrorMessage;
    isError = true;
    throw error;
  } finally {
  }
  return { isSuccess, isError };
};

const _removeSavedSearchNotification = async (savedSearchDetails) => {
  let success = false;

  try {
    if (!savedSearchDetails || !savedSearchDetails.saved_search_id) {
      savedSearchDetails =
        store.getters[MODULE_PREFIX_SAVED_SEARCH + "savedSearchDetails"];
    }

    let data = {
      name: savedSearchDetails.name,
      use_email: false,
      use_push: false,
      use_integrations: false,
      only_first_report: savedSearchDetails.onlyFirstReport,
      pause: savedSearchDetails.pause,
      params: savedSearchDetails.params,
      frequency: null,
      schedule: null,
      expiration: null,
      expiration_date: null,
      shared_list: null,
      integration_targets: null,
    };

    data = JSON.parse(JSON.stringify(data));

    await store.dispatch(
      MODULE_PREFIX_SAVED_SEARCH + MODIFY_SAVED_SEARCH_QUERY,
      {
        id: savedSearchDetails.saved_search_id,
        data: data,
      }
    );
    success = true;
  } catch (error) {
    const notificationErrorMessage =
      "Failed to remove notification for saved search query";
    error.message = error.message
      ? `${notificationErrorMessage}: ${error.message}`
      : notificationErrorMessage;
    success = false;
    throw error;
  }
  return success;
};

const _getKeywordsStringFromBuilder = (keywordsBuilder) => {
  let keywordsStr = "";
  if (keywordsBuilder) {
    let keywordsParts = [];
    if (lodashGet(keywordsBuilder, "includes")) {
      const includeGroups = [];
      keywordsBuilder.includes.forEach((group) => {
        if (lodashGet(group, "keywords") && group.keywords.length) {
          const includeWords = [];
          group.keywords.forEach((keyword) => {
            if (lodashGet(keyword, "text")) {
              includeWords.push(`"${keyword.text}"`);
            }
          });
          includeGroups.push(`+(${includeWords.join(" | ")})`);
        }
      });
      if (includeGroups.length) {
        keywordsParts.push(includeGroups.join(" "));
      }
    }
    if (lodashGet(keywordsBuilder, "excludes")) {
      const excludeWords = [];
      keywordsBuilder.excludes.forEach((keyword) => {
        if (lodashGet(keyword, "text")) {
          excludeWords.push(`-"${keyword.text}"`);
        }
      });
      keywordsParts.push(excludeWords.join(" "));
    }
    keywordsStr = keywordsParts.join(" ");
  }
  return keywordsStr;
};
